import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { AUTH_ENDPOINTS } from '../end-points/auth/auth.endpoints';
import { NotificationService } from '../services/common/notification.service';
import { isEndpointRequest } from '../utils/url-match.util';
import { extractErrorMessage } from '../utils/error-extractor.util';

const backendErrorMessage = (body: unknown): string | null => {
  if (typeof body === 'string') return body.trim() || null;
  if (typeof body !== 'object' || body === null) return null;

  const record = body as Record<string, unknown>;
  for (const key of ['detail', 'message']) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  // ValidationProblemDetails (400 from the API's ValidationActionFilter) carries the field
  // messages in `errors` and only a generic title — surface the field messages instead.
  const validationMessage = extractErrorMessage({ error: body }, '');
  if (validationMessage) return validationMessage;
  const title = record['title'];
  if (typeof title === 'string' && title.trim()) return title.trim();
  return null;
};

const httpErrorMessage = (error: HttpErrorResponse): string => {
  const backendMessage = backendErrorMessage(error.error);
  if (backendMessage) return backendMessage;

  if (error.status > 0) {
    const statusText = error.statusText.trim();
    const meaningfulStatusText = statusText && statusText.toUpperCase() !== 'OK';
    return `HTTP ${error.status}${meaningfulStatusText ? ` ${statusText}` : ''}`;
  }

  return error.message || 'Request failed';
};

// A 404 here means "this tenant has no current subscription" — an expected outcome
// the UI renders as an empty state, not a failure worth a global error toast.
const isExpectedSubscriptionLookupMiss = (req: { url: string }, error: HttpErrorResponse): boolean =>
  error.status === 404 && req.url.includes('/subscriptions/by-tenant/');

// A 404 here means "no localization defaults configured for this country" — the onboarding
// wizard then simply leaves the Localization step for the user to fill in.
const isExpectedCountryDefaultsMiss = (req: { url: string }, error: HttpErrorResponse): boolean =>
  error.status === 404 && req.url.includes('/country-packages/');

// Resend invitation for an administrator who already signed in: not a failure; the Organizations list explains it.
const isAdminAlreadySignedIn = (req: { url: string }, error: HttpErrorResponse): boolean =>
  error.status === 409 && req.url.includes('/resend-invitation')
  && (error.error as { errorCode?: string; code?: string } | null)?.errorCode === 'organizations.admin_already_signed_in';

// Finish on an organization that is already active: the onboarding page explains it in its own dialog.
const isOrganizationAlreadyActive = (error: HttpErrorResponse): boolean =>
  error.status === 409
  && (error.error as { errorCode?: string } | null)?.errorCode === 'organizations.database_already_assigned';

// HBW-044: the auth interceptor routes to the change-password page; no error toast for it.
const isPasswordChangeRequired = (error: HttpErrorResponse): boolean =>
  error.status === 403 && (error.error as { errorCode?: string } | null)?.errorCode === 'password_change_required';

// Reading documents (syscom-intelligence-api): the Smart Fill panel shows its own message.
const isDocumentReading = (req: { url: string }): boolean => req.url.includes('/v1/extractions/');

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const notification = inject(NotificationService);
  const isLoginRequest = isEndpointRequest(req.url, AUTH_ENDPOINTS.login);
  const isRefreshRequest = isEndpointRequest(req.url, AUTH_ENDPOINTS.refresh);
  // A failed sign-out is handled quietly: the local session is cleared regardless.
  const isLogoutRequest = isEndpointRequest(req.url, AUTH_ENDPOINTS.logout);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      const isSuppressed = isLoginRequest
        || isRefreshRequest
        || isLogoutRequest
        || error.status === 401
        || isExpectedSubscriptionLookupMiss(req, error)
        || isExpectedCountryDefaultsMiss(req, error)
        || isAdminAlreadySignedIn(req, error)
        || isOrganizationAlreadyActive(error)
        || isPasswordChangeRequired(error)
        || isDocumentReading(req);

      if (!isSuppressed) {
        const message = httpErrorMessage(error);
        notification.error(message);
      }

      return throwError(() => error);
    })
  );
};
